import DownloadIcon from "@mui/icons-material/Download";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  CircularProgress,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { formatJakartaEndOfDay, formatJakartaStartOfDay } from "../../utils/date";
import { forceLogoutToLogin, isUnauthorizedApiResponse } from "../../utils/unauthorized";
import {
  fetchTapLogEvents,
  selectTapLogEvents,
  selectTapLogEventsStatus,
  selectTapLogsFilters,
  selectTapLogsPagination,
  selectTapLogsSort,
  setTapLogDateRange,
  setTapLogEvent,
  setTapLogSearch,
} from "../../store/slices/tapLogsSlice";
import { selectAccessToken } from "../../store/slices/authSlice";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "/api";

const TapLogFilters = () => {
  const dispatch = useDispatch();
  const filters = useSelector(selectTapLogsFilters);
  const pagination = useSelector(selectTapLogsPagination);
  const sort = useSelector(selectTapLogsSort);
  const events = useSelector(selectTapLogEvents);
  const eventsStatus = useSelector(selectTapLogEventsStatus);
  const accessToken = useSelector(selectAccessToken);
  const [searchValue, setSearchValue] = useState(filters.search);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState(null);
  const startInputRef = useRef(null);
  const endInputRef = useRef(null);

  useEffect(() => {
    if (eventsStatus === "idle") {
      dispatch(fetchTapLogEvents());
    }
  }, [dispatch, eventsStatus]);

  useEffect(() => {
    setSearchValue(filters.search);
  }, [filters.search]);

  useEffect(() => {
    const timer = setTimeout(() => {
      dispatch(setTapLogSearch(searchValue.trim()));
    }, 350);
    return () => clearTimeout(timer);
  }, [dispatch, searchValue]);

  const queryString = useMemo(() => {
    const params = new URLSearchParams();
    params.append("page", pagination.page + 1);
    params.append("pageSize", pagination.pageSize);
    if (filters.search.trim()) {
      params.append("search", filters.search.trim());
    }
    if (filters.eventId) {
      params.append("eventId", filters.eventId);
    }
    if (filters.startDate) {
      params.append("startDate", formatJakartaStartOfDay(filters.startDate));
    }
    if (filters.endDate) {
      params.append("endDate", formatJakartaEndOfDay(filters.endDate));
    }
    if (sort.field) {
      params.append("sortBy", sort.field);
      params.append("sortDirection", sort.direction);
    }
    return params.toString();
  }, [filters, pagination, sort]);

  const handleDateChange = (key) => (event) => {
    const value = event.target.value || null;
    dispatch(
      setTapLogDateRange({
        startDate: key === "startDate" ? value : filters.startDate,
        endDate: key === "endDate" ? value : filters.endDate,
      })
    );
  };

  const openPicker = (ref) => () => {
    const input = ref.current;
    if (!input) return;
    const wasReadOnly = input.readOnly;
    input.readOnly = false;
    if (input.showPicker) {
      try {
        input.showPicker();
      } catch {
        input.focus();
      }
    } else {
      input.focus();
    }
    input.readOnly = wasReadOnly;
  };

  const handleExport = async () => {
    setExportError(null);
    setExporting(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/tap-logs/export?${queryString}`,
        {
          headers: {
            ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
          },
        }
      );
      if (isUnauthorizedApiResponse(response)) {
        forceLogoutToLogin();
        return;
      }
      if (!response.ok) {
        const payload = await response.json().catch(() => null);
        if (isUnauthorizedApiResponse(response, payload)) {
          forceLogoutToLogin();
          return;
        }
        throw new Error(
          payload?.resultDesc || response.statusText || "Failed to download file."
        );
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `tap-logs-${new Date().toISOString()}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setExportError(error.message || "Failed to download file.");
    } finally {
      setExporting(false);
    }
  };

  return (
    <Stack spacing={3}>
      <Stack
        direction={{ xs: "column", lg: "row" }}
        spacing={2}
        alignItems={{ lg: "center" }}
        justifyContent="space-between"
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          sx={{ width: { xs: "100%", lg: "auto" }, flexGrow: 1 }}
        >
          <TextField
            fullWidth
            label="Search Ticket ID / Barcode"
            placeholder="Enter ticket ID or barcode"
            value={searchValue}
            onChange={(event) => setSearchValue(event.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            select
            fullWidth
            label="Event"
            value={filters.eventId}
            onChange={(event) => dispatch(setTapLogEvent(event.target.value))}
          >
            <MenuItem value="">All events</MenuItem>
            {events.map((event) => (
              <MenuItem key={event.id} value={event.id}>
                {event.name}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: 1,
            flexShrink: 0,
          }}
        >
          <IconButton
            onClick={(event) => setMenuAnchor(event.currentTarget)}
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              width: 48,
              height: 48,
            }}
          >
            {exporting ? <CircularProgress size={20} /> : <MoreVertIcon />}
          </IconButton>
        </Box>
      </Stack>
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          elevation: 6,
          sx: {
            boxShadow: "0px 22px 45px rgba(15, 23, 42, 0.18)",
            border: "1px solid rgba(148, 163, 184, 0.16)",
            background:
              "radial-gradient(circle at top right, rgba(14, 165, 233, 0.24), transparent 55%), #ffffff",
            borderRadius: 3,
            mt: 1,
          },
        }}
      >
        <MenuItem
          onClick={() => {
            setMenuAnchor(null);
            handleExport();
          }}
          disabled={exporting}
        >
          <DownloadIcon fontSize="small" style={{ marginRight: 8 }} />
          {exporting ? "Preparing..." : "Download Excel"}
        </MenuItem>
      </Menu>

      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        sx={{ width: "100%" }}
      >
        <TextField
          fullWidth
          type="date"
          label="Start Date"
          InputLabelProps={{ shrink: true }}
          value={filters.startDate || ""}
          onChange={handleDateChange("startDate")}
          inputRef={startInputRef}
          onClick={openPicker(startInputRef)}
          InputProps={{ readOnly: true }}
          inputProps={{ style: { cursor: "pointer" } }}
          sx={{ minWidth: { sm: 220 } }}
        />
        <TextField
          fullWidth
          type="date"
          label="End Date"
          InputLabelProps={{ shrink: true }}
          value={filters.endDate || ""}
          onChange={handleDateChange("endDate")}
          inputRef={endInputRef}
          onClick={openPicker(endInputRef)}
          InputProps={{ readOnly: true }}
          inputProps={{ style: { cursor: "pointer" } }}
          sx={{ minWidth: { sm: 220 } }}
        />
      </Stack>
      {exporting && (
        <Stack direction="row" spacing={1} alignItems="center">
          <CircularProgress size={16} />
          <Typography variant="body2" color="text.secondary">
            Preparing Excel file, this may take a moment...
          </Typography>
        </Stack>
      )}
      {exportError && (
        <Typography variant="body2" color="error">
          {exportError}
        </Typography>
      )}
    </Stack>
  );
};

export default TapLogFilters;
